/**
 * ProductApplicabilityEditor — inline edytor dla `validTo` i
 * `applicabilityConstraint` w `BillingProduct` (AC-13).
 *
 * In-place edit (Product nie jest temporally versioned w archetypie); zmiana
 * idzie przez PATCH /admin/billing/products/:id i emituje
 * billing:product:applicability_changed.
 */
import React, { useState } from "react";
import { updateProduct } from "../../../../services/adminProductCatalogApi";
import type { BillingProduct } from "../../../../types/adminBilling";
import { ApplicabilityConstraintForm } from "./ApplicabilityConstraintForm";

interface Props {
  product: BillingProduct;
  brandId: string;
  onSaved: () => void;
}

export const ProductApplicabilityEditor: React.FC<Props> = ({
  product,
  brandId,
  onSaved,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [validTo, setValidTo] = useState<string>(
    product.validTo ? new Date(product.validTo).toISOString().slice(0, 16) : "",
  );
  const [constraint, setConstraint] = useState<Record<string, unknown>>(
    () => product.applicabilityConstraint ?? { type: "alwaysTrue" },
  );
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await updateProduct(product.id, brandId, {
        validTo: validTo ? `${validTo}:00+02:00` : null,
        applicabilityConstraint: constraint,
      });
      setIsEditing(false);
      onSaved();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message ?? "Nieoczekiwany błąd");
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentConstraint = product.applicabilityConstraint;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-neutral-900">
          Stosowalność i ważność
        </h4>
        {!isEditing && (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="text-xs text-primary-600 hover:text-primary-800 font-medium"
          >
            Edytuj
          </button>
        )}
      </div>

      {!isEditing ? (
        <div className="bg-neutral-50 p-3 rounded text-xs space-y-2">
          <div>
            <span className="font-medium text-neutral-700">Obowiązuje do:</span>{" "}
            <span className="text-neutral-600">
              {product.validTo
                ? new Date(product.validTo).toLocaleString("pl-PL")
                : "∞ (bez końca)"}
            </span>
          </div>
          <div>
            <span className="font-medium text-neutral-700">
              Warunek stosowalności:
            </span>
            <pre className="mt-1 p-2 bg-white border border-neutral-200 rounded overflow-x-auto">
              {JSON.stringify(
                currentConstraint ?? { type: "alwaysTrue" },
                null,
                2,
              )}
            </pre>
          </div>
        </div>
      ) : (
        <div className="bg-neutral-50 p-4 rounded space-y-3 border border-neutral-200">
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">
              Obowiązuje do (opcjonalne)
            </label>
            <input
              type="datetime-local"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
              disabled={isSubmitting}
              className="w-full text-sm border border-neutral-300 rounded p-1.5"
            />
            <p className="text-xs text-neutral-500 mt-1">
              Pusty = bez końca. Czas polski (Europe/Warsaw).
            </p>
          </div>
          <ApplicabilityConstraintForm
            value={constraint}
            onChange={setConstraint}
            disabled={isSubmitting}
          />
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              disabled={isSubmitting}
              className="px-3 py-1 text-sm text-neutral-700 hover:bg-neutral-200 rounded"
            >
              Anuluj
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={isSubmitting}
              className="px-3 py-1 text-sm bg-primary-600 text-white hover:bg-primary-700 rounded disabled:opacity-50"
            >
              {isSubmitting ? "Zapisywanie..." : "Zapisz"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
